import React from 'react';
import StandardModal from '../../Shared/UI/StandardModal/components/StandardModal';
import StandardModalLayout from '../../Shared/UI/StandardModalLayout/components/StandardModalLayout';
import Form from '../../Shared/UI/Form/components/Form';
import Field from '../../Shared/UI/Field/components/Field';
import Input from '../../Shared/UI/Input/components/Input';
import Checkbox from '../../Shared/UI/Checkbox/components/Checkbox';
import CheckboxGroup from '../../Shared/UI/CheckboxGroup/components/CheckboxGroup';
import ChoiceRow from '../../Shared/UI/ChoiceRow/components/ChoiceRow';
import FormActions from '../../Shared/UI/FormActions/components/FormActions';
import Button from '../../Shared/UI/Button/components/Button';
import ActionRow from '../../Shared/UI/ActionRow/components/ActionRow';

import {
  useBiopharmaGroupsState,
  useBiopharmaGroupsActions
} from './BiopharmaGroupsContext';

import {
  getBiopharmaGroups,
} from './restApi';

import styles from './BiopharmaGroupsFilterOptions.module.scss';

const BiopharmaGroupsFilterOptions = () => {
  const biopharmaGroupsState = useBiopharmaGroupsState();
  const {
    onResponse,
    onError,
    openSearchModal,
    closeSearchModal,
    openCategoryModal,
    closeCategoryModal,
    openStateModal,
    closeStateModal,
    openCountryModal,
    closeCountryModal,
    openMunicipalityModal,
    closeMunicipalityModal,
    updateFuzzySearchName,
    selectAllCategories,
    deselectAllCategories,
    selectAllStates,
    deselectAllStates,
    selectAllCountries,
    deselectAllCountries,
    selectAllMunicipalities,
    deselectAllMunicipalities,
    toggleStateFilterOption,
    toggleCountryFilterOption,
    toggleMunicipalityFilterOption,
    toggleCategoryFilterOption
  } = useBiopharmaGroupsActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

  const applyFilterAndClose = (closeModal) => {
    getBiopharmaGroups({
      onResponse,
      onError,
      body: {
        authenticityToken,
        pageNumber: 1,
        filterOptions: biopharmaGroupsState?.filterOptions,
      },
    });
    closeModal();
  };

  return (
    <>
      <ActionRow align="start" wrap>
        <Button
          variant="primary"
          onClick={() => {
            openSearchModal();
          }}
        >
          Search
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            openCategoryModal();
          }}
        >
          Category
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            openStateModal();
          }}
        >
          State
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            openCountryModal();
          }}
        >
          Country
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            openMunicipalityModal();
          }}
        >
          Municipality
        </Button>
      </ActionRow>

      {/* Search Modal */}
      <StandardModal
        isOpen={biopharmaGroupsState?.searchModalIsOpen}
        contentLabel="Fuzzy Search Filter"
        onRequestClose={() => {
          closeSearchModal();
        }}
      >
        <Form onSubmit={() => applyFilterAndClose(closeSearchModal)}>
          <Field label="Fuzzy Search">
            <Input
              type="text"
              value={biopharmaGroupsState?.filterOptions?.fuzzy_search_name || ''}
              onChange={(e) => {
                updateFuzzySearchName(e.target.value);
              }}
            />
          </Field>
          <FormActions>
            <Button variant="secondary" onClick={closeSearchModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Apply Filter
            </Button>
          </FormActions>
        </Form>
      </StandardModal>

      {/* Category Modal */}
      <StandardModal
        isOpen={biopharmaGroupsState?.categoryModalIsOpen}
        contentLabel="Filter By Category"
        onRequestClose={() => {
          closeCategoryModal();
        }}
      >
        <StandardModalLayout
          header={<h3>Filter By Category</h3>}
          body={
            <Form>
              <Field>
                <CheckboxGroup>
                  {Object.entries(biopharmaGroupsState?.filterOptions?.categories || {})?.map((option) => (
                    <ChoiceRow key={option[0]}>
                      <Checkbox
                        checked={option[1]}
                        onChange={() => {
                          toggleCategoryFilterOption(option[0]);
                        }}
                      >
                        {option[0]}
                      </Checkbox>
                    </ChoiceRow>
                  ))}
                </CheckboxGroup>
              </Field>
            </Form>
          }
          footer={
            <FormActions>
              <Button
                variant="secondary"
                onClick={() => {
                  selectAllCategories();
                }}
              >
                Select All
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  deselectAllCategories();
                }}
              >
                Deselect All
              </Button>
              <Button variant="secondary" onClick={closeCategoryModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => applyFilterAndClose(closeCategoryModal)}>
                Apply Filter
              </Button>
            </FormActions>
          }
        />
      </StandardModal>

      {/* State Modal */}
      <StandardModal
        isOpen={biopharmaGroupsState?.stateModalIsOpen}
        contentLabel="Filter By State"
        onRequestClose={() => {
          closeStateModal();
        }}
      >
        <StandardModalLayout
          header={<h3>Filter By State</h3>}
          body={
            <Form>
              <Field>
                <CheckboxGroup>
                  {Object.entries(biopharmaGroupsState?.filterOptions?.states || {})?.map((option) => (
                    <ChoiceRow key={option[0]}>
                      <Checkbox
                        checked={option[1]}
                        onChange={() => {
                          toggleStateFilterOption(option[0]);
                        }}
                      >
                        {option[0]}
                      </Checkbox>
                    </ChoiceRow>
                  ))}
                </CheckboxGroup>
              </Field>
            </Form>
          }
          footer={
            <FormActions>
              <Button
                variant="secondary"
                onClick={() => {
                  selectAllStates();
                }}
              >
                Select All
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  deselectAllStates();
                }}
              >
                Deselect All
              </Button>
              <Button variant="secondary" onClick={closeStateModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => applyFilterAndClose(closeStateModal)}>
                Apply Filter
              </Button>
            </FormActions>
          }
        />
      </StandardModal>

      {/* Country Modal */}
      <StandardModal
        isOpen={biopharmaGroupsState?.countryModalIsOpen}
        contentLabel="Filter By Country"
        onRequestClose={() => {
          closeCountryModal();
        }}
      >
        <StandardModalLayout
          header={<h3>Filter By Country</h3>}
          body={
            <Form>
              <Field>
                <CheckboxGroup>
                  {Object.entries(biopharmaGroupsState?.filterOptions?.countries || {})?.map((option) => (
                    <ChoiceRow key={option[0]}>
                      <Checkbox
                        checked={option[1]}
                        onChange={() => {
                          toggleCountryFilterOption(option[0]);
                        }}
                      >
                        {option[0]}
                      </Checkbox>
                    </ChoiceRow>
                  ))}
                </CheckboxGroup>
              </Field>
            </Form>
          }
          footer={
            <FormActions>
              <Button
                variant="secondary"
                onClick={() => {
                  selectAllCountries();
                }}
              >
                Select All
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  deselectAllCountries();
                }}
              >
                Deselect All
              </Button>
              <Button variant="secondary" onClick={closeCountryModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => applyFilterAndClose(closeCountryModal)}>
                Apply Filter
              </Button>
            </FormActions>
          }
        />
      </StandardModal>

      {/* Municipality Modal */}
      <StandardModal
        isOpen={biopharmaGroupsState?.municipalityModalIsOpen}
        contentLabel="Filter By Municipality"
        onRequestClose={() => {
          closeMunicipalityModal();
        }}
      >
        <StandardModalLayout
          header={<h3>Filter By Municipality</h3>}
          body={
            <Form>
              <Field>
                <CheckboxGroup>
                  {Object.entries(biopharmaGroupsState?.filterOptions?.municipalities || {})?.map((option) => (
                    <ChoiceRow key={option[0]}>
                      <Checkbox
                        checked={option[1]}
                        onChange={() => {
                          toggleMunicipalityFilterOption(option[0]);
                        }}
                      >
                        {option[0]}
                      </Checkbox>
                    </ChoiceRow>
                  ))}
                </CheckboxGroup>
              </Field>
            </Form>
          }
          footer={
            <FormActions>
              <Button
                variant="secondary"
                onClick={() => {
                  selectAllMunicipalities();
                }}
              >
                Select All
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  deselectAllMunicipalities();
                }}
              >
                Deselect All
              </Button>
              <Button variant="secondary" onClick={closeMunicipalityModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => applyFilterAndClose(closeMunicipalityModal)}>
                Apply Filter
              </Button>
            </FormActions>
          }
        />
      </StandardModal>
    </>
  );
};

export default BiopharmaGroupsFilterOptions;